import { pickSeriesSlotColor } from "../colors.js";

/**
 * Plan how to reconcile the chart's series with the auto-selected signals for the
 * current question — the pure core of useAutoSelectSignals (kept here so it's
 * unit-testable; the hook is a thin effect wrapper that applies the plan via
 * removeSeries/addSeries under a once-per-question guard).
 *
 * The goal: auto-select refreshes PER QUESTION. It replaces the *previous*
 * auto-plotted series, always PRESERVES the user's manual picks, and fills up to
 * `max` slots (the two-axis chart). So it works even when a series is already
 * shown — unlike a naive "only when empty" gate.
 *
 * Rules:
 *  - Manual picks (series without `_auto === true`) are never removed, and cap the
 *    room for auto-plots: capacity = max - manualCount.
 *  - Auto picks that duplicate a manual pick (by display name) are skipped.
 *  - Keep an auto pick already on the chart; remove prior auto series not re-picked;
 *    add the newly-picked ones into the remaining slots.
 *  - Color each add by its final slot (same palette a manual suggestion click uses).
 *
 * @param {Array<{name?: string, signalId?: string}>} autoSelect
 * @param {{ series?: object, theme?: string, max?: number }} opts
 *   series — the live `{ [name]: { _auto?: boolean, ... } }` map.
 * @returns {{ toRemove: string[], toAdd: Array<{name,ssid,color}> }}
 */
export function planAutoSelect(autoSelect, { series = {}, theme, max = 2 } = {}) {
  const entries = Object.entries(series || {});
  const manual = entries.filter(([, v]) => !v || v._auto !== true).map(([k]) => k);
  const autoNow = entries.filter(([, v]) => v && v._auto === true).map(([k]) => k);
  const lower = (s) => String(s).toLowerCase();
  const manualLower = new Set(manual.map(lower));
  const presentLower = new Set(entries.map(([k]) => lower(k)));
  const capacity = Math.max(0, max - manual.length);

  // The auto picks we want, in order, up to capacity — well-formed, not shadowing
  // a manual pick, de-duplicated by name.
  const picks = [];
  const picksLower = new Set();
  for (const s of Array.isArray(autoSelect) ? autoSelect : []) {
    if (picks.length >= capacity) break;
    const name = s && s.name;
    const ssid = s && s.signalId;
    if (!name || !ssid) continue;
    const nl = lower(name);
    if (manualLower.has(nl) || picksLower.has(nl)) continue;
    picks.push({ name, ssid });
    picksLower.add(nl);
  }

  // Drop prior auto series we're not keeping; add picks not already on the chart.
  const toRemove = autoNow.filter((k) => !picksLower.has(lower(k)));
  const toAdd = [];
  // Color each new add for the next FREE slot — after the manual picks AND ALL
  // already-plotted picks we're keeping (they hold their own colors). Count the
  // kept picks UP FRONT (regardless of their position in the pick order) so a new
  // add before a later kept pick still can't grab an occupied slot's color.
  // Clamped to the two-axis chart's slots (0/1).
  const keptCount = picks.filter((p) => presentLower.has(lower(p.name))).length;
  let slot = manual.length + keptCount;
  for (const p of picks) {
    if (presentLower.has(lower(p.name))) continue; // already plotted — keep as-is
    toAdd.push({
      name: p.name,
      ssid: p.ssid,
      color: pickSeriesSlotColor(theme, Math.min(slot, 1)),
    });
    slot += 1;
  }
  return { toRemove, toAdd };
}
